import { defineStore } from "pinia";
import { TankGroup, Tank } from "@/types/v2";
import { useTasksStore } from "@/stores/v2";

export const useTanksStore = defineStore({
  id: "tanks",
  state: () => ({
    tanks: [] as Tank[],
    activeTank: {} as Tank,
    activeGroup: {} as TankGroup,
  }),
  getters: {
    // Getters
    getTanks(state) {
      return state.tanks;
    },
    getActiveTank(state) {
      return state.activeTank;
    },
    getActiveGroup(state) {
      return state.activeGroup;
    },
  },
  actions: {
    // Initial
    async init() {
      // return (this.tanks = await TanksService.fetchTanksByTaskId(taskKey));
    },
    setActiveTank(tank: Tank) {
      return (this.activeTank = tank);
    },
    setTanks(tanks: Tank[]) {
      return (this.tanks = tanks);
    },
    setActiveGroup(group: TankGroup) {
      this.activeGroup = group;
    },
    setActiveTankByKey(tankKey: string) {
      const tank = this.tanks.find((t) => t.key === tankKey);
      if (tank !== undefined) {
        this.activeTank = tank;
        return tank;
      }

      return null;
    },
    getTankById(key: string) {
      return this.tanks.find((t) => t.key === key);
    },
    getTanksByTaskId(taskId: string) {
      return this.tanks.filter((t) => t.key === taskId);
    },
    updateTanksAfterCheck(tank: any): Tank | null {
      const updatedTanks = [...this.tanks];
      const tankIndex = updatedTanks.findIndex((t) => t.key === tank.key);

      if (tankIndex !== -1) {
        updatedTanks[tankIndex] = tank;
        this.tanks = updatedTanks;
        const tasksStore = useTasksStore();
        tasksStore.updateTaskTankData(tank);

        return tank;
      }

      return null;
    },
    updateTankGroupsData(tankKey: string, groups: TankGroup[]): boolean {
      const updatedTanks = this.tanks.map((tank) =>
        tank.key === tankKey ? { ...tank, groups } : tank
      );
      const tankIndex = this.tanks.findIndex((t) => t.key === tankKey);

      if (tankIndex !== -1) {
        this.tanks = updatedTanks;
        return true;
      }

      return false;
    },
    updateActiveTankSingleGroupData(group: TankGroup): Tank | null {
      const updatedGroups = this.activeTank.groups?.map((g) =>
        g.key === group.key ? { ...g, ...group } : g
      );
      const groupToUpdate = updatedGroups?.find((g) => g.key === group.key);

      if (groupToUpdate) {
        this.activeTank = { ...this.activeTank, groups: updatedGroups };

        if (this.activeTank.key) {
          this.updateTankGroupByKey(this.activeTank.key, groupToUpdate);
        }

        return this.activeTank;
      }

      return null;
    },
    updateTankGroupByKey(tankKey: string, group: TankGroup): boolean {
      const updatedTanks = this.tanks.map((tank) => {
        if (tank.key === tankKey) {
          const updatedGroups = tank.groups?.map((g) =>
            g.name === group.name ? { ...g, ...group } : g
          );
          return { ...tank, groups: updatedGroups || [] };
        }
        return tank;
      });

      const tankIndex = this.tanks.findIndex((t) => t.key === tankKey);
      if (tankIndex !== -1) {
        this.tanks = updatedTanks;
        return true;
      }

      return false;
    },
    updateActiveTankData(data: any): void {
      if (!this.activeTank) {
        console.error("No active tank to update");
        return;
      }

      const updatedTank = {
        ...this.activeTank,
        status: "Completed",
        measures: data,
      };

      this.activeTank = updatedTank;
      this.updateTanksAfterCheck(updatedTank);
    },
    updateGroupMortalityData(data: object): void {
      if (!this.activeGroup) {
        console.error("No active group to update");
        return;
      }

      const dataArray = Array.isArray(data) ? data : [data];

      let progressDataArray = [] as Array<any>;

      if (this.activeGroup.progressData) {
        if (Array.isArray(this.activeGroup.progressData)) {
          progressDataArray = this.activeGroup.progressData;
        } else {
          // If progressData is an object, convert it to an array
          progressDataArray.push(this.activeGroup.progressData);
        }
      }

      // Calculate the total length of progressData arrays from all groups
      const totalProgressDataLength = (this.activeTank.groups || [])
        .map((group) => group.progressData)
        .flat().length;

      const updatedProgressData = dataArray.map((item, i) => {
        return {
          ...item,
          index: totalProgressDataLength + i,
        };
      });

      const updatedGroup = {
        ...this.activeGroup,
        progressData: [...progressDataArray, ...updatedProgressData],
      };

      // Update the active tank first & get the updated tank
      const tank = this.updateActiveTankSingleGroupData(updatedGroup);

      // if the tank is not null
      if (tank) {
        // Update the active task with the new active tank data
        const tasksStore = useTasksStore();
        tasksStore.updateTaskTankData(tank);
      }
    },
  },
});
